// src/components/ProductDetails/ProductDetails.jsx
import './ProductDetails.css';
import VariantSelector from "./sub/OptionSelector/VariantSelector.jsx";
import {useEffect, useMemo, useState} from "react";
import ImageGallery from "./sub/ImageGallery/ImageGallery.jsx";
import {addToCart} from "../../CartHandler.js";
import {useBasket} from "../../context/BasketContext/BasketContext.jsx";
import {useNotification} from "../../context/NotificationContext/NotificationContext.jsx";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {checkStock, fetchProductDetails, getProductVariants} from "../../ProductsHandler.js";
import {handleAmountChange} from "../Basket/BasketItem/BasketItem.jsx";
import {Helmet} from "react-helmet-async";
import RecommendedItemsBar from "./sub/RecommendedItemsBar/RecommendedItemsBar.jsx";
import DOMPurify from "dompurify";

export default function ProductDetails() {
    const [amount, setAmount] = useState(1);
    const [product, setProduct] = useState(undefined);
    const [selectedVariant, setSelectedVariant] = useState(null);
    const [variants, setVariants] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [sellableVariants, setSellableVariants] = useState([]);
    const [maxQty, setMaxQty] = useState(null);

    const {bumpBasketVersion} = useBasket();
    const {setDisplayNotification} = useNotification();
    const [searchParams] = useSearchParams();
    const [productPrice, setProductPrice] = useState(null);
    const {handle: handleParam} = useParams();
    const navigate = useNavigate();
    const {pathname, search} = useLocation();
    const legacyId = searchParams.get("id");
    const [isInStock, setIsInStock] = useState(false); // używane do pokazania komunikatu „Produkt chwilowo niedostępny”

    const getVariantValuesArray = (variantNode) =>
        (variantNode.options || []).map(o => o.value);

    const selectionIsComplete = useMemo(() => (
        product && selectedOptions.length === product.variants.length && selectedOptions.every(v => v !== "")
    ), [product, selectedOptions]);


    useEffect(() => {
        if (selectedOptions.every(v => v !== "") && product !== undefined) {
            const result = product.variantList.find(variant => variant.title === selectedOptions.join(" / "));
            console.log("RES",result)
            console.log(result.price.discountedPrice)
            if(result.price.discountedPrice==null){
                setProductPrice(result.price.price);
            }else{
                setProductPrice(result.price.discountedPrice);
            }
            console.log("PRICE",productPrice);
        }
    }, [selectedOptions]);

    useEffect(() => {
        const normalizedHandle = handleParam ? handleParam.trim() : "";
        const normalizedLegacyId = legacyId ? legacyId.trim() : "";

        if (!normalizedHandle && !normalizedLegacyId) {
            navigate("/404", {replace: true});
            return;
        }

        setProduct(undefined);
        setSelectedVariant(null);
        setSelectedOptions([]);
        setSellableVariants([]);
        setMaxQty(null);
        setAmount(1);

        (async () => {
            const data = await fetchProductDetails(
                normalizedHandle
                    ? {handle: normalizedHandle}
                    : {id: normalizedLegacyId}
            );
            if (!data) {
                navigate("/404", {replace: true});
                return;
            }
            if (!normalizedHandle && data.handle) {
                navigate(`/produkt/${data.handle}`, {replace: true});
            }

            setProduct(data);
            setProductPrice(data.price.price);
            setSelectedOptions(Array(data.variants.length).fill(""));


            if (data.variantList.length === 1) {
                setSelectedVariant(data.variantList[0].id);
            }

            const v = await getProductVariants(data.id);
            setVariants(v);

            const results = await Promise.all(
                data.variantList.map(async vItem => {
                    try {
                        const qty = await checkStock(vItem.id);
                        return qty > 0 ? {...vItem, quantityAvailable: qty} : null;
                    } catch {
                        return null;
                    }
                })
            );
            setSellableVariants(results.filter(Boolean));

        })();
    }, [handleParam, legacyId, navigate]);

    const selectedVariantId = useMemo(() => {
        if (!product || !selectionIsComplete) return null;

        const match = sellableVariants.find(vItem => {
            const values = getVariantValuesArray(vItem);
            if (values.length !== selectedOptions.length) return false;
            for (let i = 0; i < values.length; i++) {
                if (selectedOptions[i] !== values[i]) return false;
            }
            return true;
        });

        return match ? match.id : null;
    }, [product, selectionIsComplete, sellableVariants, selectedOptions]);

    // 🔁 NOWY, minimalny efekt do obsługi „brak w magazynie”
    useEffect(() => {
        if (!product) {
            setIsInStock(false);
            return;
        }

        // 1) Jeżeli żaden wariant nie ma stanu (sellableVariants puste)
        if (sellableVariants.length === 0) {
            setIsInStock(true);
            return;
        }

        // 2) Przy produktach z wieloma wariantami:
        //    jeśli użytkownik wybrał wszystkie opcje, ale nie ma takiego wariantu z dostępnością
        if (product.variantList.length > 1 && selectionIsComplete && !selectedVariantId) {
            setIsInStock(true);
            return;
        }

        // Inaczej – nie pokazujemy komunikatu
        setIsInStock(false);
    }, [product, sellableVariants, selectionIsComplete, selectedVariantId]);

    useEffect(() => {
        let active = true;
        (async () => {
            if (!selectedVariantId) {
                setMaxQty(null);
                return;
            }
            try {
                const qty = await checkStock(selectedVariantId);
                if (!active) return;
                const stock = Number.isFinite(qty) ? qty : null;
                setMaxQty(stock);
                if (stock != null && amount > stock) {
                    setAmount(stock > 0 ? stock : 1);
                }
            } catch {
                if (active) setMaxQty(null);
            }
        })();
        return () => {
            active = false;
        };
    }, [selectedVariantId]); // eslint-disable-line react-hooks/exhaustive-deps

    const atMax = maxQty != null && amount >= maxQty;
    const canAddToCartQuantityOK = maxQty == null ? true : amount <= maxQty;

    const addToCartEnabled =
        (!!selectedVariantId || (product?.variantList?.length === 1 && sellableVariants.length === 1))
        && canAddToCartQuantityOK;

    const isOptionSelectable = (groupIndex, candidateValue) => {
        if (!product) return false;

        return sellableVariants.some(vItem => {
            const vValues = getVariantValuesArray(vItem);
            if (vValues[groupIndex] !== candidateValue) return false;
            for (let i = 0; i < selectedOptions.length; i++) {
                const chosen = selectedOptions[i];
                if (i === groupIndex) continue;
                if (chosen && vValues[i] !== chosen) return false;
            }
            return true;
        });
    };

    const resolvedVariantId = useMemo(() => {
        if (selectedVariantId) return selectedVariantId;
        if (product?.variantList?.length === 1 && sellableVariants.length === 1) {
            return product.variantList[0].id;
        }
        return null;
    }, [selectedVariantId, product, sellableVariants]);

    const imageUrls = product?.images
        ? (Array.isArray(product.images)
            ? product.images.map(i => typeof i === "string" ? i : (i?.url || i?.src)).filter(Boolean)
            : [])
        : [];

    const stripHtml = (html) => {
        if (!html) return "";
        const tmp = typeof window !== "undefined" ? document.createElement("div") : null;
        if (tmp) {
            tmp.innerHTML = html;
            return (tmp.textContent || tmp.innerText || "").trim();
        }
        return String(html).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
    };

    const safeDescriptionHtml = useMemo(() => {
        const html = product?.descriptionHtml || "";
        return DOMPurify.sanitize(html, {USE_PROFILES: {html: true}});
    }, [product?.descriptionHtml]);

    // 🧠 SEO preferencje z Shopify (z fallbackiem)
    const seoTitle = product?.seo?.title || (product ? `${product.name} | Lumestore` : "Produkt | Lumestore");
    const metaDescription = product?.seo?.description
        || (product?.description || stripHtml(product?.descriptionHtml) || "").slice(0, 155);

    const canonical = product?.handle
        ? `https://lumestore.pl/produkt/${product.handle}`
        : `https://lumestore.pl${pathname}${search || ""}`;

    const openGraphImage = imageUrls[0] || "https://lumestore.pl/og-image.webp";

    const productJsonLd = product ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: product.name,
        description: product?.seo?.description || product.description || stripHtml(product.descriptionHtml) || "",
        image: imageUrls.length ? imageUrls : undefined,
        sku: product.sku || undefined,
        brand: product.brand ? {"@type": "Brand", name: (product.brand.name || product.brand)} : undefined,
        offers: {
            "@type": "Offer",
            url: canonical,
            priceCurrency: "PLN",
            price: String(product.price?.discountedPrice ?? product.price?.price ?? ""),
            availability: (maxQty == null ? (sellableVariants.length > 0) : maxQty > 0)
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock"
        }
    } : null;

    const breadcrumbsJsonLd = product ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
            {
                "@type": "ListItem",
                "position": 1,
                "name": "Produkty",
                "item": "https://lumestore.pl/produkty"
            },
            {
                "@type": "ListItem",
                "position": 2,
                "name": product.name,
                "item": canonical
            }
        ]
    } : null;

    return (
        <div id='product-details-container'>
            {product !== undefined && (
                <>
                    <Helmet>
                        <title>{seoTitle}</title>
                        <meta name="description" content={metaDescription || "fallback desc"}/>
                        <link rel="canonical" href={canonical}/>
                        <meta property="og:type" content="product"/>
                        <meta property="og:url" content={canonical}/>
                        <meta property="og:title" content={seoTitle}/>
                        <meta property="og:description" content={metaDescription}/>
                        <meta property="og:image" content={openGraphImage}/>
                        <meta property="og:site_name" content="Lumestore"/>
                        <meta name="twitter:card" content="summary_large_image"/>
                        <meta name="twitter:title" content={seoTitle}/>
                        <meta name="twitter:description" content={metaDescription}/>
                        <meta name="twitter:image" content={openGraphImage}/>

                        {productJsonLd && (
                            <script type="application/ld+json">
                                {JSON.stringify(productJsonLd)}
                            </script>
                        )}
                        {breadcrumbsJsonLd && (
                            <script type="application/ld+json">
                                {JSON.stringify(breadcrumbsJsonLd)}
                            </script>
                        )}
                    </Helmet>

                    <div id='product-details-top'>
                        <div id='product-details-top-left'>
                            <ImageGallery images={product.images}/>
                        </div>

                        <div id='product-details-top-right'>
                            <h1 id='product-details-name'>{product.name}</h1>

                            <div id='product-details-price'>
                <span id='product-details-price-label-1'>
                  <span id={`product-details-price${product.price.discountedPrice != null ? '--discounted' : ""}`}>
                    {Number(
                        product.price.discountedPrice !== null ?
                            product.price.discountedPrice :
                            productPrice
                    ).toFixed(2)} zł
                  </span>
                    {product.price.discountedPrice != null && (
                        <span id='product-details-price--discount'>
                            {console.log("ASD",productPrice)}
                      {Number(productPrice).toFixed(2)} zł
                    </span>
                    )}
                </span>
                                <span id='product-details-price-label-2'>Brutto</span>
                            </div>

                            <div
                                id='product-details-description'
                                className='product-details-rich-description'
                                dangerouslySetInnerHTML={{__html: safeDescriptionHtml || (product?.description || "")}}
                            />

                            <div id='product-details-variants-container'>
                                {product.variantList.length > 1 && (

                                    <>
                                        {product.variants.map((variantGroup, groupIndex) => (
                                            <VariantSelector
                                                key={groupIndex}
                                                variant={variantGroup}
                                                parentIndex={groupIndex}
                                                selectedOptions={selectedOptions}
                                                setSelectedOptions={setSelectedOptions}
                                                isOptionSelectable={isOptionSelectable}
                                            />
                                        ))}
                                    </>
                                )}
                                {isInStock && (
                                    <span id='product-lack-in-store'>
                                        Produkt chwilowo niedostępny
                                    </span>
                                )}
                            </div>

                            <div id='product-details-management-container'>
                                <div id='product-details-amount-container'>
                                    <div
                                        id='product-details-amount-substract-bttn'
                                        onClick={() => handleAmountChange(null, 'substraction', amount, setAmount)}
                                        className='product-details-amount-bttn'
                                    >-
                                    </div>

                                    <input
                                        type='number'
                                        id='product-details-amount-input'
                                        value={amount}
                                        min={1}
                                        onChange={(e) => {
                                            const next = Math.max(1, parseInt(e.target.value || "1", 10));
                                            setAmount(maxQty != null ? Math.min(next, maxQty) : next);
                                        }}
                                    />

                                    <div
                                        id='product-details-amount-add-bttn'
                                        onClick={() => {
                                            if (!atMax) handleAmountChange(null, 'addition', amount, setAmount);
                                        }}
                                        className={`product-details-amount-bttn ${atMax ? 'amount-bttn-disabled' : ''}`}
                                        aria-disabled={atMax}
                                        title={atMax && maxQty != null ? `Maksymalna ilość: ${maxQty}` : undefined}
                                    >+
                                    </div>
                                </div>

                                {addToCartEnabled ? (
                                    <div
                                        id='product-details-add-to-cart-bttn'
                                        onClick={async () => {
                                            if (!resolvedVariantId) return;
                                            await addToCart(resolvedVariantId, amount);
                                            bumpBasketVersion();
                                            setDisplayNotification({
                                                type: "message",
                                                message: `Produkt${amount > 1 ? 'y' : ''} pomyślnie dodano do koszyka`
                                            });
                                        }}
                                    >
                                        DODAJ DO KOSZYKA
                                    </div>
                                ) : (
                                    <div id='product-details-add-to-cart-bttn' className='add-to-cart-disabled'>
                                        DODAJ DO KOSZYKA
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    <div id='product-details-bottom'>
                        <span id='recommended-items-label'>ZOBACZ TAKŻE</span>
                        <RecommendedItemsBar/>
                    </div>
                </>
            )}
        </div>
    );
}
